/**
 * Workflow Improvement #001 — the Vacancy Request rendered as the initial
 * Position Profile (v1) on the admin recruitment detail page.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, FileText } from 'lucide-react';

const F = ({ label, value }) => (value == null || value === '' ? null : (
  <div>
    <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
    <p className="text-sm">{value}</p>
  </div>
));

export function VacancyProfileCard({ vacancy }) {
  const { t } = useTranslation();
  if (!vacancy) return null;

  const downloadJd = async () => {
    try {
      const res = await api.get(`/admin/vacancy-requests/${vacancy.id}/jd-file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = vacancy.jd_file?.filename || 'jd';
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="p-5 shadow-card border-primary/20" data-testid="vacancy-profile-card">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">{t('recruit2.profile.cardTitle', { defaultValue: 'Perfil de la Posición — v1' })}</h3>
        <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/20">
          {t('recruit2.profile.fromVacancy', { defaultValue: 'Desde Solicitud de Vacante' })}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        {t('recruit2.profile.cardHint', { defaultValue: 'Información capturada por el cliente al crear la vacante. Revísala: si es suficiente, aprueba el perfil directamente.' })}
      </p>
      <div className="grid sm:grid-cols-3 gap-3">
        <F label={t('vacancy.department', { defaultValue: 'Departamento' })} value={vacancy.department} />
        <F label={t('vacancy.openings', { defaultValue: 'Nº de plazas' })} value={vacancy.openings} />
        <F label={t('vacancy.employmentType', { defaultValue: 'Tipo de empleo' })} value={t(`vacancy.employmentTypes.${vacancy.employment_type}`, { defaultValue: vacancy.employment_type })} />
        <F label={t('vacancy.startDate', { defaultValue: 'Inicio deseado' })} value={vacancy.desired_start_date} />
        <F label={t('vacancy.priority', { defaultValue: 'Prioridad' })} value={t(`vacancy.priorities.${vacancy.hiring_priority}`, { defaultValue: vacancy.hiring_priority })} />
        <F label={t('vacancy.location', { defaultValue: 'Ubicación' })} value={vacancy.work_location} />
        <F label={t('vacancy.schedule', { defaultValue: 'Horario' })} value={vacancy.work_schedule && t(`vacancy.schedules.${vacancy.work_schedule}`, { defaultValue: vacancy.work_schedule })} />
        <F label={t('vacancy.education', { defaultValue: 'Educación' })} value={vacancy.education_level && t(`vacancy.educationLevels.${vacancy.education_level}`, { defaultValue: vacancy.education_level })} />
        <F label={t('vacancy.experience', { defaultValue: 'Años de experiencia' })} value={vacancy.years_experience} />
        <F label={t('vacancy.gender', { defaultValue: 'Género preferido' })} value={vacancy.preferred_gender !== 'any' ? t(`vacancy.genders.${vacancy.preferred_gender}`, { defaultValue: vacancy.preferred_gender }) : null} />
        <F label={t('vacancy.ageRange', { defaultValue: 'Rango de edad' })} value={vacancy.age_range ? `${vacancy.age_range.min}–${vacancy.age_range.max}` : null} />
        <F label={t('vacancy.budget', { defaultValue: 'Presupuesto' })} value={vacancy.budget_defined ? `${vacancy.budget_from} – ${vacancy.budget_to}` : null} />
      </div>
      {(vacancy.languages || []).length > 0 && (
        <div className="mt-3">
          <F label={t('vacancy.languages', { defaultValue: 'Idiomas' })}
            value={vacancy.languages.map((l) => `${l.language} (${t(`vacancy.proficiencies.${l.proficiency}`, { defaultValue: l.proficiency })})`).join(', ')} />
        </div>
      )}
      <div className="mt-3 space-y-3">
        <F label={t('vacancy.technical', { defaultValue: 'Conocimientos técnicos' })} value={vacancy.technical_knowledge} />
        <F label={t('vacancy.competencies', { defaultValue: 'Competencias descritas' })} value={vacancy.competencies_text} />
        <F label={t('vacancy.comments', { defaultValue: 'Comentarios' })} value={vacancy.comments} />
      </div>
      {vacancy.jd_file && (
        <Button size="sm" variant="outline" className="gap-1.5 mt-4" onClick={downloadJd} data-testid="vacancy-profile-jd-download">
          <Download className="h-3.5 w-3.5" /> {vacancy.jd_file.filename}
        </Button>
      )}
    </Card>
  );
}

export default VacancyProfileCard;
